/**
 * SignalParamPopover Component
 *
 * Popover form for editing signal parameters.
 * Uses a portal to render above all other content, positioned relative to anchor.
 */

import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SIGNAL_REGISTRY, ParamSchema } from "../../../../../config/signals";
import { TickerDropdown } from "../../../../../components/common/TickerDropdown";
import { InfoTooltip } from "../../../../../components/common/InfoTooltip";
import styles from "./SignalParamPopover.module.css";

interface SignalParamPopoverProps {
  signalType: string;
  params: Record<string, unknown>;
  onParamsChange: (params: Record<string, unknown>) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

export const SignalParamPopover: React.FC<SignalParamPopoverProps> = ({
  signalType,
  params,
  onParamsChange,
  onClose,
  anchorRef,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const schema = SIGNAL_REGISTRY[signalType];
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position based on anchor element
  // Use fixed positioning and continuously track the pencil's viewport position
  useEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current || !popoverRef.current) return;

      const rect = anchorRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      // Position above the anchor (subtract popover height + gap)
      const top = rect.top - popoverRect.height - 4;

      // Position to the left (align right edge of popover with right edge of anchor)
      const left = rect.right - popoverRect.width;

      // Use viewport coordinates (fixed positioning)
      // The popover will stay anchored to the pencil as the container scrolls
      setPosition({
        top: Math.max(8, top), // Ensure at least 8px from top of viewport
        left: Math.max(8, left), // Ensure at least 8px from left of viewport
      });
    };

    // Initial position update
    updatePosition();

    // Update again after a brief delay to account for popover rendering
    const timeoutId = setTimeout(updatePosition, 0);

    // Find the scrollable ancestor and listen for scroll events
    let scrollableAncestor: HTMLElement | null = anchorRef.current;
    while (scrollableAncestor) {
      const style = window.getComputedStyle(scrollableAncestor);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll') {
        break;
      }
      scrollableAncestor = scrollableAncestor.parentElement;
    }

    // Listen to scroll on both the scrollable ancestor and window
    if (scrollableAncestor) {
      scrollableAncestor.addEventListener('scroll', updatePosition);
    }
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      if (scrollableAncestor) {
        scrollableAncestor.removeEventListener('scroll', updatePosition);
      }
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  // Close on click outside (but not on the anchor itself)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);
      const isOutsideAnchor = anchorRef.current && !anchorRef.current.contains(target);

      if (isOutsidePopover && isOutsideAnchor) {
        onClose();
      }
    };

    // Delay adding listener to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!schema) return null;

  const paramEntries = Object.entries(schema.params);
  if (paramEntries.length === 0) {
    return createPortal(
      <div
        ref={popoverRef}
        className={styles.popover}
        style={{ top: position.top, left: position.left }}
      >
        <div className={styles.noParams}>No parameters required</div>
      </div>,
      document.body
    );
  }

  const handleParamChange = (key: string, value: unknown) => {
    onParamsChange({ ...params, [key]: value });
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
    >
      <div className={styles.header}>
        <span className={styles.title}>{schema.label}</span>
      </div>
      <div className={styles.content}>
        {paramEntries.map(([key, paramSchema]) => (
          <ParamInput
            key={key}
            paramKey={key}
            schema={paramSchema}
            value={params[key]}
            onChange={(value) => handleParamChange(key, value)}
            signalType={signalType}
          />
        ))}
      </div>
    </div>,
    document.body
  );
};

// Individual parameter input renderer
interface ParamInputProps {
  paramKey: string;
  schema: ParamSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  signalType: string;
}

const ParamInput: React.FC<ParamInputProps> = ({ paramKey, schema, value, onChange, signalType }) => {
  const signalSchema = SIGNAL_REGISTRY[signalType];

  const normalizeNumericDisplay = React.useCallback((num: number, decimals = 10) => {
    // Avoid floating-point artifacts in controlled number inputs (e.g. 7.000000000000001)
    return Number(num.toFixed(decimals));
  }, []);

  // Validate parameter value
  const validationError = React.useMemo(() => {
    if (signalSchema?.validateParam && value !== undefined) {
      const result = signalSchema.validateParam(paramKey, value);
      return result === true ? null : result;
    }
    return null;
  }, [signalSchema, paramKey, value]);

  const renderInput = () => {
    const hasError = validationError !== null;

    switch (schema.type) {
      case "symbol":
        return (
          <TickerDropdown
            value={(value as string) ?? ""}
            onChange={(val) => onChange(val || undefined)}
            placeholder={schema.placeholder}
          />
        );

      case "select":
        return (
          <select
            className={styles.select}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value || undefined)}
          >
            <option value="">Select...</option>
            {schema.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "frequency": {
        const freq = (value as { every?: number; unit?: string } | undefined) ?? {};
        const everyIsFixedToOne = signalType === "intraday_ma_crossover" && paramKey === "frequency";
        const every = freq.every;
        const unit = freq.unit ?? "";
        const emit = (nextEvery: number | undefined, nextUnit: string) => {
          if ((nextEvery === undefined || Number.isNaN(nextEvery)) && !nextUnit) {
            onChange(undefined);
            return;
          }
          // Allow undefined every value when unit is selected
          if (nextEvery === undefined || Number.isNaN(nextEvery)) {
            onChange({
              every: everyIsFixedToOne ? 1 : undefined,
              unit: (nextUnit || "seconds") as "seconds" | "minutes" | "hours",
            });
            return;
          }
          onChange({
            every: everyIsFixedToOne ? 1 : nextEvery,
            unit: (nextUnit || "seconds") as "seconds" | "minutes" | "hours",
          });
        };
        return (
          <div className={styles.inputWrapper}>
            <span className={styles.prefix}>Every</span>
            <input
              type="number"
              className={styles.input}
              value={everyIsFixedToOne ? 1 : (every ?? "")}
              min={1}
              step={1}
              placeholder="1"
              disabled={everyIsFixedToOne}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  // Allow clearing the field
                  emit(undefined, unit);
                  return;
                }
                const parsed = parseInt(raw, 10);
                emit(parsed, unit);
              }}
            />
            <select
              className={styles.select}
              value={unit}
              onChange={(e) => emit(everyIsFixedToOne ? 1 : every, e.target.value)}
            >
              <option value="">unit…</option>
              <option value="seconds">Seconds</option>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
            </select>
          </div>
        );
      }

      case "percent":
      case "dollar":
      case "number":
      case "integer":
        return (
          <div className={styles.inputWrapper}>
            {schema.type === "dollar" && <span className={styles.prefix}>$</span>}
            <input
              type="number"
              className={`${styles.input} ${hasError ? styles.inputError : ''}`}
              value={
                schema.type === "percent" && typeof value === "number"
                  ? normalizeNumericDisplay(value * 100)  // Convert decimal to percentage for display (0.03 -> 3)
                  : (value as number) ?? ""
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  onChange(undefined);
                } else {
                  const numVal = schema.type === "integer" ? parseInt(val, 10) : parseFloat(val);
                  // For percent type, convert from percentage to decimal (3 -> 0.03)
                  onChange(
                    schema.type === "percent"
                      ? normalizeNumericDisplay(numVal / 100, 12)
                      : numVal
                  );
                }
              }}
              placeholder={schema.placeholder}
              min={schema.min}
              max={schema.max}
              step={schema.type === "integer" ? 1 : schema.step ?? 0.01}
            />
            {schema.type === "percent" && <span className={styles.suffix}>%</span>}
          </div>
        );

      case "date":
        return (
          <input
            type="date"
            className={styles.input}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        );

      default:
        return (
          <input
            type="text"
            className={styles.input}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            placeholder={schema.placeholder}
          />
        );
    }
  };

  return (
    <div className={styles.paramRow}>
      <label className={styles.label}>
        {schema.label}
        {schema.required && <span className={styles.required}>*</span>}
        {schema.description && <InfoTooltip text={schema.description} />}
      </label>
      {renderInput()}
      {validationError && (
        <span className={styles.error}>{validationError}</span>
      )}
    </div>
  );
};
